import React, { useMemo, useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
// ChartJS is registered in atrieve-location-report-card when imported elsewhere
import ProfileCover from 'src/sections/student/profile-cover';
import MyTodosCard from 'src/sections/career/my-todos-card';
import { useAuthContext } from 'src/auth/hooks';
import { SCHOOLS } from 'src/constants';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import {
  useGetCollegiateInterestReport,
  useGetStudentInterestReport,
  useGetCaseload,
} from 'src/hooks/use-career-api';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CaseloadCard from 'src/sections/career/caseload-card';
import { albertaToday } from 'src/sections/career/next-visit';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

export default function CareersDashboardView() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const schoolIdDefault =
    user?.admin?.homeSchool ||
    user?.admin?.accessSchools?.filter((s) => s)[0] ||
    [0]?.toString() ||
    null;
  // const today = new Date();
  // const currentYear =
  //   today.getMonth() >= 8 // September is month 8 (0-based)
  //     ? today.getFullYear() + 1
  //     : today.getFullYear();

  // LocalStorage keys
  const LS_LOCATION_KEY = 'careersDashboard.selectedLocation';
  // const LS_YEAR_KEY = 'careersDashboard.selectedYear';

  // Get initial values from localStorage or fallback
  // The school in the URL (the Monday digest's links carry it) wins over the one remembered
  const [searchParams, setSearchParams] = useSearchParams();
  const getInitialLocation = () =>
    searchParams.get('school') || localStorage.getItem(LS_LOCATION_KEY) || schoolIdDefault;
  // const getInitialYear = () => Number(localStorage.getItem(LS_YEAR_KEY)) || currentYear;

  const [selectedLocation, setSelectedLocation] = useState(getInitialLocation);
  // const [selectedYear, setSelectedYear] = useState(getInitialYear);

  useEffect(() => {
    localStorage.setItem(LS_LOCATION_KEY, selectedLocation);
  }, [selectedLocation]);
  // useEffect(() => {
  //   localStorage.setItem(LS_YEAR_KEY, selectedYear);
  // }, [selectedYear]);

  // Determine allowedSchools based on user role
  const isStudentCareerAdmin = user?.roles?.includes('studentcareer.admin');
  // Coordinators see the dashboard as counsellors do, for the schools they can reach
  const isSchoolCounselor = user?.roles
    ?.map((r) => r.toLowerCase())
    .some((r) => r === 'school.counselor' || r === 'off_campus.coordinator');
  const hasTodos = user?.roles?.some((r) =>
    ['school.counselor', 'off_campus.coordinator', 'super.admin'].includes(String(r).toLowerCase())
  );
  const allSchools = SCHOOLS;
  const allowedSchools = useMemo(() => {
    if (isStudentCareerAdmin) return allSchools;
    if (isSchoolCounselor && Array.isArray(user?.admin?.accessSchools)) {
      return SCHOOLS.filter((s) => user.admin.accessSchools.includes(s.schoolNumber || s.value));
    }
    return [];
  }, [isStudentCareerAdmin, isSchoolCounselor, user, allSchools]);

  const school = useMemo(
    () =>
      allowedSchools.find((s) => s.schoolNumber === Number(selectedLocation))?.schoolName ||
      'School',
    [selectedLocation, allowedSchools]
  );

  const name = school;

  // Only show the school selector if more than 1 allowed school
  const showSchoolSelector = allowedSchools.length > 1;
  const [report, setReport] = useState(null);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const { collegiateInterestReport, collegiateInterestReportLoading } =
    useGetCollegiateInterestReport(selectedLocation, true);
  const { studentInterestReport, studentInterestReportLoading } = useGetStudentInterestReport(
    selectedLocation,
    true
  );
  const { caseload, caseloadToday, caseloadAi, caseloadLoading, caseloadError, revalidateCaseload } =
    useGetCaseload(selectedLocation);

  useEffect(() => {
    if (collegiateInterestReport) {
      setReport(collegiateInterestReport);
      if (Array.isArray(collegiateInterestReport.grades)) {
        setSelectedGrades(collegiateInterestReport.grades);
      }
    }
  }, [collegiateInterestReport]);

  // Prepare chart data when report is available: totals by interest across selected grades
  const chartData = useMemo(() => {
    if (!report) return null;
    const palette = [
      '#4e79a7',
      '#f28e2b',
      '#e15759',
      '#76b7b2',
      '#59a14f',
      '#edc949',
      '#af7aa1',
      '#ff9da7',
      '#9c755f',
      '#bab0ab',
    ];
    const grades = report.grades || [];
    const interests = report.interests || [];
    const selectedSet = new Set(selectedGrades && selectedGrades.length ? selectedGrades : grades);
    const totalsByInterest = (report.seriesByInterest || []).map((series) => {
      let total = 0;
      series.data.forEach((count, idx) => {
        const grade = grades[idx];
        if (selectedSet.has(grade)) total += count || 0;
      });
      return total;
    });
    const backgroundColor = interests.map((_, idx) => palette[idx % palette.length]);
    return {
      labels: interests,
      datasets: [
        {
          label: 'Total Students',
          data: totalsByInterest,
          backgroundColor,
        },
      ],
    };
  }, [report, selectedGrades]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Collegiate Interests (Totals for Selected Grades)' },
        tooltip: {
          callbacks: {
            title: (items) => items?.[0]?.label || '',
            label: (ctx) => {
              const total = ctx.dataset?.data?.[ctx.dataIndex] ?? 0;
              return `Students: ${total}`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Interest' } },
        y: { beginAtZero: true, title: { display: true, text: 'Students' } },
      },
    }),
    []
  );

  // Prepare word cloud data from studentInterestReport (ignore grade)
  const wordCloud = useMemo(() => {
    const totals = studentInterestReport?.totals?.byInterest;
    const interestList = studentInterestReport?.interests;
    if (!totals || !interestList) return null;
    const entries = interestList.map((name, idx) => ({
      name,
      count: totals[name] || 0,
      idx,
    }));
    const max = Math.max(1, ...entries.map((e) => e.count));
    const minFont = 12;
    const maxFont = 36;
    const palette = [
      '#4e79a7',
      '#f28e2b',
      '#e15759',
      '#76b7b2',
      '#59a14f',
      '#edc949',
      '#af7aa1',
      '#ff9da7',
      '#9c755f',
      '#bab0ab',
    ];
    return entries
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count)
      .map((e) => ({
        text: e.name,
        value: e.count,
        color: palette[e.idx % palette.length],
        fontSize: Math.round(minFont + (e.count / max) * (maxFont - minFont)),
      }));
  }, [studentInterestReport]);

  // Dialog state for word cloud clicks
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [selectedInterestSource, setSelectedInterestSource] = useState(null);
  const barRef = useRef(null);

  const studentsByAsn = useMemo(
    () => new Map((caseload || []).map((s) => [String(s.asn), s])),
    [caseload]
  );

  const selectedInterestStudents = useMemo(() => {
    if (!selectedInterest) return [];
    let byGrade = null;
    if (selectedInterestSource === 'student') {
      byGrade = studentInterestReport?.details?.[selectedInterest] || {};
    } else if (selectedInterestSource === 'collegiate') {
      byGrade = report?.details?.[selectedInterest] || {};
    } else {
      return [];
    }
    let gradeList = [];
    if (selectedInterestSource === 'collegiate') {
      gradeList = selectedGrades && selectedGrades.length ? selectedGrades : report?.grades || [];
    } else {
      gradeList = Object.keys(byGrade).map((g) => Number(g));
    }
    const allowedGrades = new Set(gradeList);
    const allAsns = Object.entries(byGrade)
      .filter(([g]) => allowedGrades.has(Number(g)))
      .flatMap(([, list]) => list);
    const rows = allAsns.map((asn) => {
      const s = studentsByAsn.get(String(asn));
      return {
        asn: String(asn),
        fullName: s?.name || null,
        grade_level: typeof s?.grade === 'number' ? s.grade : null,
      };
    });
    rows.sort((a, b) => {
      const an = (a.fullName || '').toLowerCase();
      const bn = (b.fullName || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      const ag = a.grade_level ?? 0;
      const bg = b.grade_level ?? 0;
      return ag - bg;
    });
    return rows;
  }, [
    selectedInterest,
    selectedInterestSource,
    studentInterestReport,
    report,
    selectedGrades,
    studentsByAsn,
  ]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Card sx={{ mb: 4, height: 220, position: 'relative' }}>
        <ProfileCover name={name} schoolCode={selectedLocation} />
      </Card>
      <Stack spacing={4}>
        {/* Only counsellors and coordinators are given to-dos (a principal sees the reports) */}
        {hasTodos && <MyTodosCard />}
        {showSchoolSelector && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="location-select-label">School</InputLabel>
              <Select
                labelId="location-select-label"
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('school', e.target.value);
                      return next;
                    },
                    { replace: true }
                  );
                }}
                label="School"
              >
                {allowedSchools.map((school) => (
                  <MenuItem
                    key={school.schoolNumber || school.value}
                    value={school.schoolNumber?.toString() || school.value}
                  >
                    {school.schoolName || school.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
        <CaseloadCard
          rows={caseload || []}
          today={caseloadToday || albertaToday()}
          ai={caseloadAi}
          loading={caseloadLoading}
          error={caseloadError}
          schoolId={selectedLocation}
          onChanged={revalidateCaseload}
        />
        {/* Reports: the interest charts, below the caseload they support */}
        <Typography variant="h5">Reports</Typography>
        <Card>
          <Stack spacing={2} direction="row" alignItems="flex-start" justifyContent="space-between">
            <CardHeader title="Collegiate Interests Overview" />
            <CardHeader
              title={
                <>
                  {report?.grades && report.grades.length > 0 && (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      sx={{ mb: 2 }}
                      justifyContent="flex-end"
                    >
                      <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="grades-select-label">Grades</InputLabel>
                        <Select
                          labelId="grades-select-label"
                          label="Grades"
                          multiple
                          value={selectedGrades}
                          onChange={(e) => setSelectedGrades(e.target.value)}
                          renderValue={(selected) =>
                            Array.isArray(selected) ? selected.sort((a, b) => a - b).join(', ') : ''
                          }
                        >
                          {report.grades.map((g) => (
                            <MenuItem key={g} value={g}>
                              <Checkbox checked={selectedGrades.indexOf(g) > -1} />
                              <ListItemText primary={`Grade ${g}`} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  )}
                </>
              }
            />
          </Stack>
          {chartData?.labels && chartData.labels?.length > 0 && (
            <CardContent sx={{ pb: 0, pt: 2 }}>
              {collegiateInterestReportLoading && <Box sx={{ p: 2 }}>Loading chart…</Box>}
              {chartData && (
                <Bar
                  ref={barRef}
                  data={chartData}
                  options={chartOptions}
                  onClick={(event) => {
                    const chart = barRef.current;
                    if (!chart) return;
                    const elements = chart.getElementsAtEventForMode(
                      event,
                      'nearest',
                      { intersect: true },
                      false
                    );
                    if (!elements || !elements.length) return;
                    const [{ index }] = elements;
                    const interest = chartData.labels?.[index];
                    if (!interest) return;
                    setSelectedInterest(interest);
                    setSelectedInterestSource('collegiate');
                    setInterestDialogOpen(true);
                  }}
                />
              )}
            </CardContent>
          )}
          {!(chartData?.labels && chartData.labels?.length > 0) && (
            <Box sx={{ p: 4 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No collegiate interest data available for this school.
              </Typography>
            </Box>
          )}
        </Card>

        <Card sx={{ height: '100%' }}>
          <CardHeader title="Student Interests " />
          <CardContent>
            {studentInterestReportLoading && <Box sx={{ p: 2 }}>Loading interests…</Box>}
            {wordCloud && wordCloud.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                {wordCloud.map((w, i) => (
                  <Box
                    key={`${w.text}-${i}`}
                    sx={{
                      backgroundColor: `${w.color}22`,
                      border: `1px solid ${w.color}55`,
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.5,
                      cursor: 'pointer',
                    }}
                    title={`${w.text}: ${w.value}`}
                    onClick={() => {
                      setSelectedInterest(w.text);
                      setSelectedInterestSource('student');
                      setInterestDialogOpen(true);
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: w.fontSize,
                        fontWeight: w.fontSize >= 24 ? 600 : 500,
                        color: 'text.primary',
                      }}
                    >
                      {w.text} ({w.value})
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {wordCloud && wordCloud.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No student interests available for this school.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Interest Dialog */}
        <Dialog open={interestDialogOpen} onClose={() => setInterestDialogOpen(false)} fullWidth>
          <DialogTitle>
            {selectedInterest ? `Students interested in ${selectedInterest}` : 'Students'}
          </DialogTitle>
          <DialogContent dividers>
            {selectedInterest && selectedInterestStudents.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>ASN</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedInterestStudents.map((s) => (
                    <TableRow
                      key={s.asn}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setInterestDialogOpen(false);
                        navigate(`/student/${s.asn}?tab=career`);
                      }}
                    >
                      <TableCell>{s.fullName || '—'}</TableCell>
                      <TableCell>{s.grade_level ?? '—'}</TableCell>
                      <TableCell>{s.asn}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No students found for this interest.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInterestDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Container>
  );
}
